/**
 * Validate a South African ID number (13 digits, Luhn check).
 * Format: YYMMDD SSSS C A Z
 *   YYMMDD = date of birth
 *   SSSS = gender (0000-4999 female, 5000-9999 male)
 *   C = citizenship (0=SA, 1=permanent resident)
 *   A = usually 8
 *   Z = Luhn checksum digit
 */
export function validateSAIdNumber(idNumber: string): { valid: boolean; error?: string } {
  if (!idNumber) return { valid: false, error: 'ID number is required' };

  const cleaned = idNumber.replace(/\s/g, '');
  if (!/^\d{13}$/.test(cleaned)) {
    return { valid: false, error: 'ID number must be exactly 13 digits' };
  }

  // Validate date portion
  const year = parseInt(cleaned.substring(0, 2), 10);
  const month = parseInt(cleaned.substring(2, 4), 10);
  const day = parseInt(cleaned.substring(4, 6), 10);

  if (month < 1 || month > 12) {
    return { valid: false, error: 'Invalid month in ID number' };
  }
  if (day < 1 || day > 31) {
    return { valid: false, error: 'Invalid day in ID number' };
  }

  // Luhn algorithm check
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    let digit = parseInt(cleaned[i], 10);
    if (i % 2 !== 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  if (sum % 10 !== 0) {
    return { valid: false, error: 'Invalid ID number (checksum failed)' };
  }

  return { valid: true };
}

/**
 * Extract date of birth from SA ID number.
 */
export function dobFromSAId(idNumber: string): string {
  const cleaned = idNumber.replace(/\s/g, '');
  if (cleaned.length !== 13) return '';

  const yy = parseInt(cleaned.substring(0, 2), 10);
  const mm = cleaned.substring(2, 4);
  const dd = cleaned.substring(4, 6);
  const century = yy >= 0 && yy <= 30 ? '20' : '19';
  return `${century}${cleaned.substring(0, 2)}-${mm}-${dd}`;
}

/**
 * Validate South African mobile number.
 * Accepts: 0821234567, +27821234567, 27821234567
 */
export function validateSAPhone(phone: string): { valid: boolean; error?: string } {
  if (!phone) return { valid: true }; // phone is optional
  const cleaned = phone.replace(/[\s\-()]/g, '');
  const pattern = /^(\+?27|0)[1-9]\d{8}$/;
  if (!pattern.test(cleaned)) {
    return { valid: false, error: 'Invalid SA phone number (e.g. 0821234567)' };
  }
  return { valid: true };
}

/**
 * Validate email address.
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  if (!email) return { valid: true }; // email is optional
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!pattern.test(email)) {
    return { valid: false, error: 'Invalid email address' };
  }
  return { valid: true };
}

/**
 * Validate required field.
 */
export function validateRequired(value: string, fieldName: string): { valid: boolean; error?: string } {
  if (!value || !value.trim()) {
    return { valid: false, error: `${fieldName} is required` };
  }
  return { valid: true };
}
